
import React from 'react';
import { Card } from "@/components/ui/card";
import { FileBarChart } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { PerformanceData } from './types';
import { analyzeFunnelData } from './utils/funnelAnalysis';
import { useIsMobile } from '@/hooks/use-mobile';
import ResponsiveChartTooltip from './ResponsiveChartTooltip';

interface QuizFunnelAnalysisProps {
  performanceData: PerformanceData[];
}

// Define the step color function
const getStepColor = (stepIndex: number) => {
  const colors = [
    '#8B5CF6', // Purple for first step
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Yellow
    '#EF4444', // Red for last step (drop-off)
  ];
  return colors[stepIndex % colors.length];
};

export const QuizFunnelAnalysis: React.FC<QuizFunnelAnalysisProps> = ({ performanceData }) => {
  const { isMobile, screenSize } = useIsMobile();
  
  const funnelData = React.useMemo(() => {
    const steps = analyzeFunnelData(performanceData);
    return steps.map((step, index) => ({
      ...step,
      color: getStepColor(index),
      name: step.name,
      value: 100 - step.dropoffRate // Convert dropoff to completion rate for visualization
    }));
  }, [performanceData]);

  if (funnelData.length === 0) {
    return null;
  }

  // Fixed to return a proper tuple [string, string]
  const customTooltipFormatter = (value: any, name?: string): [string, string] => {
    return [`${Number(value).toFixed(1)}%`, 'Completion Rate'];
  };

  return (
    <Card className={isMobile ? "p-3" : "p-6"}>
      <h2 className={`${isMobile ? "text-base" : "text-xl"} font-semibold mb-4 flex items-center gap-2`}>
        <FileBarChart className="w-5 h-5 text-purple-500" />
        Quiz Completion Funnel
      </h2>
      
      <div className="w-full">
        <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
          <BarChart 
            data={funnelData} 
            layout="vertical" 
            margin={isMobile ? 
              { top: 5, right: 5, left: screenSize === 'xs' ? 90 : 110, bottom: 5 } : 
              { top: 10, right: 10, left: 150, bottom: 10 }
            }
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
            <XAxis 
              type="number" 
              domain={[0, 100]} 
              tickFormatter={(value) => `${value}%`}
              tick={{ fontSize: isMobile ? 10 : 12 }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis 
              dataKey="name" 
              type="category" 
              width={isMobile ? (screenSize === 'xs' ? 90 : 110) : 150}
              tick={{ 
                fontSize: isMobile ? (screenSize === 'xs' ? 9 : 10) : 12,
                fill: '#4b5563' 
              }}
              tickLine={false}
              axisLine={false}
            />
            
            <Tooltip 
              content={<ResponsiveChartTooltip formatter={customTooltipFormatter} />}
            />
            
            <Bar 
              dataKey="value" 
              radius={[0, 4, 4, 0]}
              animationDuration={800}
              animationBegin={200}
            >
              {funnelData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className={`mt-4 ${isMobile ? "text-xs" : "text-sm"} text-gray-500`}>
        Tracks user progress through the quiz-taking process, highlighting completion rates at each stage
      </div>
      
      <div className={`mt-4 flex flex-wrap ${isMobile ? "justify-center gap-2 text-[10px]" : "justify-start gap-3 text-xs"}`}>
        {funnelData.map((step, index) => (
          <div key={`legend-${index}`} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: step.color }}></div>
            <span>{step.name}: {(100 - step.dropoffRate).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </Card>
  );
};
